'use client';
import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { useIsMobile } from '@/hooks/useIsMobile';
import { customRichTextComponent, richTextComponent } from '@/sanity/sanityPortableText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import { useRef } from 'react';
import { CTAButton } from '@/shadcn/ui/button';

interface IAboutWatch {
  title1: PortableTextBlock[];
  image: GROQImage;
  title2: PortableTextBlock[];
  button: GROQButton;
  buttonMobile: GROQButton;
}

export default function AboutWatch({
  title1,
  image,
  title2,
  button,
  buttonMobile,
}: IAboutWatch) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const imageRotate = useTransform(scrollYProgress, [0, 0.15, 1], [0, 0, 12]);

  return (
    <section id="about-watch-section" className="container flex flex-col-reverse lg:flex-row items-center lg:min-h-[135rem] lg:px-[19rem] pt-[3.5rem] lg:pt-0 pb-[15rem] lg:pb-0 overflow-clip relative">
      <div className="w-full grid auto-rows-min text-center lg:text-left justify-items-stretch gap-[7rem] lg:gap-[47rem]">
        <motion.div
          initial={{ opacity: 0.3 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1] }}
          className="flex flex-col items-center lg:items-start w-full lg:w-[73.25rem] gap-[3rem] lg:gap-[3.62rem] relative z-10
          prose-headings:text-[6rem] prose-headings:text-[#F2F0EF] prose-headings:font-[300] prose-headings:uppercase prose-headings:leading-none prose-headings:mb-0
          prose-p:text-[2rem] prose-p:text-[#F2F0EF]/[72%] prose-p:font-[300] prose-p:leading-[160%] prose-p:mb-0 prose-p:tracking-[0.09rem] lg:prose-p:tracking-normal prose-p:max-w-[90vw] lg:prose-p:max-w-none lg:[&_br]:hidden"
        >
          {title1 && (
            <PortableText value={title1} components={richTextComponent} />
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0.3 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1.5, ease: [0.25, 1, 0.5, 1] }}
          className="flex flex-col w-full lg:w-[56.375rem] relative z-10 items-center lg:items-start lg:justify-self-end
          prose-headings:text-[6rem] prose-headings:text-[#F2F0EF] prose-headings:font-[300] prose-headings:uppercase prose-headings:leading-[120%] prose-headings:mb-0
          prose-p:text-[2rem] prose-p:text-[#F2F0EF]/[72%] prose-p:font-[300] prose-p:leading-[160%] prose-p:mb-0 prose-p:tracking-[0.08rem] lg:prose-p:tracking-normal"
        >
          {title2 && (
            <PortableText value={title2} components={customRichTextComponent({
              block: {
                small: ({ children }) => (
                  <p className="font-nunito !text-[#F2F0EF] !tracking-[0.53625rem] !text-[1.75rem] !font-[400] uppercase !leading-none !mb-[3.5rem] lg:!mb-[4rem]">{children}</p>
                ),
              },
            })} />
          )}

          <CTAButton className="mt-[6rem] hidden lg:block" data={button} />
          <CTAButton className="mt-[5rem] lg:hidden" data={buttonMobile} />
        </motion.div>
      </div>

      <motion.div
        ref={sectionRef}
        className="w-full lg:w-[170rem] aspect-[169/95] relative lg:absolute lg:left-[12%] lg:top-[19%] lg:-translate-x-1/2 lg:-translate-y-1/2 z-0 pointer-events-none select-none"
        style={{ rotate: isMobile ? 0 : imageRotate }}
        transition={{
          duration: 7,
          ease: [0.76, 0, 0.24, 1],
        }}
      >
        <motion.div
          className="pointer-events-none select-none"
          initial={{ opacity: 0, y: '5rem' }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 1.2,
            ease: [0.76, 0, 0.24, 1],
          }}
        >
          <GroqImage
            src={image}
            alt={image?.name}
            className="!w-full !h-full !object-cover rotate-0 lg:rotate-[-30deg] scale-125 lg:scale-100"
            quality={100}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
